// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useEffect, useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { useTranslation } from "react-i18next";
import { EReceiptsPolicy, escapeTranslationValues, } from "@sequentech/ui-core";
import { RECEIVE_BALLOT } from "../queries/ReceiveBallot";
import { provideBallotService } from "../services/BallotService";
import { castVoteErrorMessage } from "../services/CastVoteErrors";
import { CastBallotsErrorType } from "../services/VotingPortalError";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { selectReceivedBallotId, setReceivedBallot, } from "../store/receivedBallots/receivedBallotsSlice";
export var EReceiveBallotStatus;
(function (EReceiveBallotStatus) {
    EReceiveBallotStatus["NOT_REQUIRED"] = "not-required";
    EReceiveBallotStatus["PENDING"] = "pending";
    EReceiveBallotStatus["RECEIVED"] = "received";
    EReceiveBallotStatus["FAILED"] = "failed";
})(EReceiveBallotStatus || (EReceiveBallotStatus = {}));
export var isReceivedAtReview = function (ballotStyle) {
    var _a, _b;
    return ((_b = (_a = ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.ballot_eml.election_event_presentation) === null || _a === void 0 ? void 0 : _a.receipts) === null || _b === void 0 ? void 0 : _b.policy) ===
        EReceiptsPolicy.SIGNED_BY_BALLOT_BOX;
};
/**
 * Sends the voter-signed ballot to the ballot box when the review screen
 * opens. The Ballot ID is known only after the ballot box has stored and
 * signed the ballot and this device has checked that signature.
 */
export var useReceiveBallot = function (_a) {
    var _b;
    var ballotStyle = _a.ballotStyle, auditableBallot = _a.auditableBallot, ballotHash = _a.ballotHash, isMultiContest = _a.isMultiContest, skip = _a.skip;
    var t = useTranslation().t;
    var dispatch = useAppDispatch();
    var receiveBallot = useMutation(RECEIVE_BALLOT)[0];
    var _c = provideBallotService(), toHashableBallot = _c.toHashableBallot, toHashableMultiBallot = _c.toHashableMultiBallot, verifyReceivedBallot = _c.verifyReceivedBallot;
    var required = isReceivedAtReview(ballotStyle) && !skip;
    var electionId = (_b = ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.election_id) !== null && _b !== void 0 ? _b : "";
    var receivedBallotId = useAppSelector(selectReceivedBallotId(electionId, ballotHash));
    var _d = useState(), failure = _d[0], setFailure = _d[1];
    // The same ballot is sent once: going back to the ballot makes a new one.
    var sentBallotHash = useRef(undefined);
    useEffect(function () {
        if (!required ||
            !ballotStyle ||
            !auditableBallot ||
            !ballotHash ||
            receivedBallotId ||
            sentBallotHash.current === ballotHash) {
            return;
        }
        sentBallotHash.current = ballotHash;
        setFailure(undefined);
        var setErrorMsg = function (errorMsg) { return setFailure({ errorMsg: errorMsg }); };
        var send = function () { return __awaiter(void 0, void 0, void 0, function () {
            var ballotBoxKey, voter_signing_pk, voter_ballot_signature, answer, hashableBallot, result, received, error_1, receivedBallot;
            var _a, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        ballotBoxKey = ballotStyle.ballot_eml.ballot_box_key;
                        voter_signing_pk = auditableBallot.voter_signing_pk, voter_ballot_signature = auditableBallot.voter_ballot_signature;
                        if (!ballotBoxKey || !voter_signing_pk || !voter_ballot_signature) {
                            setErrorMsg(t("reviewScreen.error.".concat(CastBallotsErrorType.CAST_VOTE)));
                            return [2 /*return*/];
                        }
                        _c.label = 1;
                    case 1:
                        _c.trys.push([1, 3, , 4]);
                        hashableBallot = isMultiContest
                            ? toHashableMultiBallot(auditableBallot)
                            : toHashableBallot(auditableBallot);
                        return [4 /*yield*/, receiveBallot({
                                variables: {
                                    electionId: ballotStyle.election_id,
                                    ballotId: ballotHash,
                                    content: JSON.stringify(hashableBallot),
                                },
                            })];
                    case 2:
                        result = _c.sent();
                        received = (_a = result.data) === null || _a === void 0 ? void 0 : _a.receive_ballot;
                        if (result.error || !received) {
                            console.log((_b = result.error) === null || _b === void 0 ? void 0 : _b.message);
                            setErrorMsg(t("reviewScreen.error.".concat(CastBallotsErrorType.UNABLE_TO_FETCH_DATA)));
                            return [2 /*return*/];
                        }
                        answer = received;
                        return [3 /*break*/, 4];
                    case 3:
                        error_1 = _c.sent();
                        console.log(error_1);
                        setErrorMsg(castVoteErrorMessage(error_1, t));
                        return [2 /*return*/];
                    case 4:
                        receivedBallot = {
                            tenant_id: ballotStyle.tenant_id,
                            election_event_id: ballotStyle.election_event_id,
                            election_id: ballotStyle.election_id,
                            ballot_hash: ballotHash,
                            voter_signing_pk: voter_signing_pk,
                            voter_ballot_signature: voter_ballot_signature,
                            received_at: answer.received_at,
                            key_id: answer.key_id,
                            received_signature: answer.received_signature,
                            ballot_id: answer.ballot_id,
                        };
                        try {
                            verifyReceivedBallot(ballotBoxKey, receivedBallot);
                        }
                        catch (error) {
                            console.error("The ballot box's receipt does not verify:", error);
                            setErrorMsg(t("reviewScreen.error.".concat(CastBallotsErrorType.INCONSISTENT_HASH), escapeTranslationValues({
                                ballotId: answer.ballot_id,
                                auditableBallotHash: ballotHash,
                            })));
                            return [2 /*return*/];
                        }
                        dispatch(setReceivedBallot({ electionId: ballotStyle.election_id, receivedBallot: receivedBallot }));
                        return [2 /*return*/];
                }
            });
        }); };
        send();
    }, [required, ballotStyle, auditableBallot, ballotHash, receivedBallotId]);
    if (!required) {
        return { status: EReceiveBallotStatus.NOT_REQUIRED };
    }
    if (receivedBallotId) {
        return { status: EReceiveBallotStatus.RECEIVED, ballotId: receivedBallotId };
    }
    if (failure) {
        return { status: EReceiveBallotStatus.FAILED, errorMsg: failure.errorMsg };
    }
    return { status: EReceiveBallotStatus.PENDING };
};
